import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TaskCardMolecule } from '../TaskCard';
import CardStackMolecule from '../CardStack/CardStackMolecule';
import type { ViewMode } from '../CardStack/CardStackMolecule';
import type { Task } from '../TaskList/TaskListMolecule.types';
import type { StatusValue } from '../StatusCheckInDialog/CheckInFormContent';
import { useFeedTaskCheckIn } from './useFeedTaskCheckIn';
import './FeedTaskStackMolecule.scss';

function getStorageKey(sphereId: string): string {
  return `feed:${sphereId}:taskStackCollapsed`;
}

function loadCollapsed(sphereId: string): boolean {
  try {
    return localStorage.getItem(getStorageKey(sphereId)) === 'true';
  } catch {
    return false;
  }
}

function saveCollapsed(sphereId: string, collapsed: boolean): void {
  try {
    localStorage.setItem(getStorageKey(sphereId), String(collapsed));
  } catch {
    // Quota or access error — silently ignore
  }
}

function isUserAssigned(task: Task, userId: string): boolean {
  if (task.responsibleUsers?.some(u => u.userId === userId)) return true;
  if (task.assignedPerspectives?.some(p => p.assigned?.some(a => a.id === userId))) return true;
  if (task.assignee?.id === userId) return true;
  return false;
}

function filterUrgentTasks(tasks: Task[], userId: string): Task[] {
  return tasks.filter(task => {
    if (!isUserAssigned(task, userId)) return false;
    return task.status === 'overdue' || (task.urgency ?? 0) >= 4;
  });
}

function sortTasks(tasks: Task[]): Task[] {
  return [...tasks].sort((a, b) => {
    const aOverdue = a.status === 'overdue' ? 1 : 0;
    const bOverdue = b.status === 'overdue' ? 1 : 0;
    if (aOverdue !== bOverdue) return bOverdue - aOverdue;
    if (aOverdue && bOverdue) {
      const dateA = new Date(a.dueAt || '').getTime() || 0;
      const dateB = new Date(b.dueAt || '').getTime() || 0;
      return dateA - dateB;
    }
    return (b.urgency ?? 0) - (a.urgency ?? 0);
  });
}

export interface FeedTaskStackMoleculeProps {
  /** All tasks from context (filtering is done internally) */
  tasks: Task[];
  /** Current user's graph sphere ID */
  currentUserId: string;
  /** Active sphere ID (used for localStorage key) */
  sphereId: string;
  /** Callback when a task card is clicked */
  onTaskClick?: (task: Task) => void;
  /** Callback when check-in action is triggered on a task */
  onCheckIn?: (task: Task) => void;
  /** Callback when inline check-in form is submitted */
  onCheckInSubmit?: (statusValue: StatusValue, comment?: string) => void;
}

const FeedTaskStackMolecule: React.FC<FeedTaskStackMoleculeProps> = ({
  tasks,
  currentUserId,
  sphereId,
  onTaskClick,
  onCheckIn,
  onCheckInSubmit,
}) => {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(() => loadCollapsed(sphereId));
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | 'auto'>('auto');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [viewMode, setViewMode] = useState<ViewMode>('stack');
  const checkIn = useFeedTaskCheckIn(onCheckInSubmit);

  const filteredTasks = useMemo(
    () => sortTasks(filterUrgentTasks(tasks, currentUserId)),
    [tasks, currentUserId]
  );

  // Clamp currentIndex when filteredTasks changes
  useEffect(() => {
    if (filteredTasks.length > 0 && currentIndex >= filteredTasks.length) {
      setCurrentIndex(filteredTasks.length - 1);
    }
  }, [filteredTasks.length, currentIndex]);

  // Animate content collapse/expand
  useEffect(() => {
    if (contentRef.current) {
      if (collapsed) {
        setContentHeight(contentRef.current.scrollHeight);
        requestAnimationFrame(() => setContentHeight(0));
      } else {
        setContentHeight(contentRef.current.scrollHeight);
        const timer = setTimeout(() => setContentHeight('auto'), 300);
        return () => clearTimeout(timer);
      }
    }
  }, [collapsed, filteredTasks.length]);

  const handleToggle = useCallback(() => {
    setCollapsed(prev => {
      const next = !prev;
      saveCollapsed(sphereId, next);
      return next;
    });
  }, [sphereId]);

  const handleNavigateNext = useCallback(() => {
    setCurrentIndex(prev => Math.min(prev + 1, filteredTasks.length - 1));
  }, [filteredTasks.length]);

  const handleNavigatePrev = useCallback(() => {
    setCurrentIndex(prev => Math.max(prev - 1, 0));
  }, []);

  const handleToggleViewMode = useCallback(() => {
    setViewMode(prev => (prev === 'stack' ? 'fan' : 'stack'));
  }, []);

  if (filteredTasks.length === 0) return null;

  const hasOverdue = filteredTasks.some(task => task.status === 'overdue');
  const headerKey = hasOverdue ? 'feed.taskStack.header' : 'feed.taskStack.headerUrgent';

  return (
    <div className={`feed-task-stack-molecule${collapsed ? ' feed-task-stack-molecule--collapsed' : ''}`}>
      <button
        className="feed-task-stack-molecule__header"
        onClick={handleToggle}
        type="button"
        aria-expanded={!collapsed}
      >
        <span className="feed-task-stack-molecule__header-title">
          {t(headerKey, { count: filteredTasks.length })}
        </span>
        <i className={`pi ${collapsed ? 'pi-chevron-down' : 'pi-chevron-up'}`} />
      </button>

      <div
        ref={contentRef}
        className="feed-task-stack-molecule__content"
        style={{ height: typeof contentHeight === 'number' ? `${contentHeight}px` : contentHeight }}
        aria-hidden={collapsed}
      >
        <div className="feed-task-stack-molecule__content-inner">
          <CardStackMolecule
            currentIndex={currentIndex}
            totalCount={filteredTasks.length}
            viewMode={viewMode}
            onNavigateNext={handleNavigateNext}
            onNavigatePrev={handleNavigatePrev}
            onToggleViewMode={handleToggleViewMode}
            compact={true}
            navigationLayout="vertical"
          >
            {filteredTasks.map(task => (
              <TaskCardMolecule
                key={task.id}
                task={task}
                currentUserId={currentUserId}
                onClick={onTaskClick}
                onCheckIn={onCheckIn}
                isCheckInExpanded={checkIn.expandedCheckInTaskId === task.id}
                onCheckInExpandChange={checkIn.handleExpandChange}
                checkInSuccess={checkIn.checkInSuccessTaskId === task.id}
                onCheckInSubmit={checkIn.handleSubmit}
                isCheckInSubmitting={checkIn.isCheckInSubmitting && checkIn.expandedCheckInTaskId === task.id}
                checkInError={checkIn.expandedCheckInTaskId === task.id ? checkIn.checkInError : null}
              />
            ))}
          </CardStackMolecule>
        </div>
      </div>
    </div>
  );
};

export default FeedTaskStackMolecule;
